import { QRCodeSVG } from "qrcode.react";
import { useAuthContext } from "../../../../context/AuthContext";
import { useFrontofficeContext } from "../../../../context/FrontofficeContext";
import { useLanguageContext } from "../../../../context/LanguageContext";
import useCustomEffect from "../../../../hooks/useCustomEffect";
import useEvaApi, { EvaApiCall } from "../../../../hooks/useEvaApi";
import { renderToString } from "react-dom/server";
import MessagesStepView from "./MessagesStepView";
import HtmlHelper from "../../../../helpers/HtmlHelper";

function MessagesStep({ controller }) {
  const callEvaApi = useEvaApi();
  const authContext = useAuthContext();
  const { translateEntity, translate, language } = useLanguageContext();
  const { terminals, terminal } = useFrontofficeContext();

  // Get the messages for the step
  const messages = controller.step.messages
    .filter((message) => message.type === "screen")
    .map((message) => getMessageContent(message));

  /**
   * UseEffect for initializing
   */
  useCustomEffect(() => {
    fetchNeededRelations();
  });

  /**
   * This method will return the content for the message
   * @param {object} message
   * @returns {string}
   */
  function getMessageContent(message) {
    let content = translateEntity(message.message_template, "message");
    if (!content) return null;

    // Normalize the tags that the rich text editor may have split up with html tags
    content = HtmlHelper.normalizeMessageTags(content);

    const replacements = getMessageReplacements();
    Object.keys(replacements).forEach((key) => {
      content = content.replaceAll(`[${key}]`, replacements[key] ?? "");
    });

    // Clean up any remaining fields that were not replaced (because it was not filled in for example)
    content = content.replace(/\[field_[^\]]+]/g, "");

    return content;
  }

  /**
   * This method will return the replacements for the message
   * @returns {object}
   */
  function getMessageReplacements() {
    return {
      ...getMessageVisitorReplacements(),
      ...getMessageFieldReplacements(),
    };
  }

  /**
   * This method will return the visitor replacements for the message
   * @returns {object}
   */
  function getMessageVisitorReplacements() {
    const employee = controller.fetchedEntities.employees.find(
      (emp) =>
        parseInt(emp.id) === parseInt(controller.visitorData.employee_id),
    );

    const accessCommanderPin = controller.visitor?.external_ids?.find(
      (id) => id.name === "vis_access_commander_2n_pin",
    )?.external_id;

    return {
      vis_company_name: authContext.auth.user?.company?.name ?? "",
      vis_location_name:
        authContext.auth.user?.company?.locations?.find(
          (loc) => loc.id === authContext.auth.location,
        )?.name ?? "",
      vis_location_address:
        authContext.auth.user?.company?.locations?.find(
          (loc) => loc.id === authContext.auth.location,
        )?.address ?? "",
      vis_visitor_type_name: translateEntity(controller.visitorType, "label"),
      vis_language_label: language?.label ?? "",
      vis_sign_in_terminal_name:
        terminals.find(
          (t) =>
            parseInt(t.id) === parseInt(controller.visitor?.sign_in_terminal),
        )?.name ??
        terminal?.name ??
        "",
      vis_employee_title: employee?.title ?? "",
      vis_employee_name: employee?.name ?? "",
      vis_employee_first_name: employee?.first_name ?? "",
      vis_employee_last_name: employee?.last_name ?? "",
      vis_email: controller.visitor?.email ?? "",
      vis_registration_code: controller.visitor?.registration_code ?? "",
      vis_registration_code_qr: controller.visitor?.registration_code
        ? renderToString(
            <QRCodeSVG value={controller.visitor?.registration_code} />,
          )
        : controller.visitor?.registration_code,
      vis_returning: controller.visitor?.returning
        ? translate(
            `eva.main.general.${controller.visitor.returning ? "yes" : "no"}`,
          )
        : (controller.visitor?.returning ?? ""),
      vis_expected_date:
        controller.visitor?.expected_date_formatted ??
        controller.visitor?.expected_date ??
        "",
      vis_expected_end_date:
        controller.visitor?.expected_end_date_formatted ??
        controller.visitor?.expected_end_date ??
        "",
      vis_expected_time:
        controller.visitor?.expected_time_formatted ??
        controller.visitor?.expected_time ??
        "",
      vis_signed_in_at:
        controller.visitor?.signed_in_at_formatted ??
        controller.visitor?.signed_in_at ??
        "",
      vis_signed_out_at:
        controller.visitor?.signed_out_at_formatted ??
        controller.visitor?.signed_out_at ??
        "",
      vis_status: controller.visitor?.status
        ? translate(`eva.main.visitor_statuses.${controller.visitor.status}`)
        : (controller.visitor?.status ?? ""),
      vis_salto_user_pin:
        controller.visitor?.external_ids?.find(
          (id) => id.name === "salto_user_pin",
        )?.external_id ?? "",
      vis_access_commander_2n_pin_qr: accessCommanderPin
        ? renderToString(<QRCodeSVG value={accessCommanderPin} />)
        : accessCommanderPin,
      vis_access_commander_2n_pin: accessCommanderPin,
      vis_license_plate: controller.visitor?.license_plate ?? "",
      vis_approval_status: controller.visitor?.approval_status ?? "",
    };
  }

  /**
   * This method will return the field replacements for the message
   * @returns {object}
   */
  function getMessageFieldReplacements() {
    return Object.keys(controller.visitorData)
      .filter((key) => key.startsWith("field_"))
      .reduce((acc, key) => {
        const fieldId = key.split("_")[1];
        const field = controller.fetchedEntities.fields.find(
          (field) => parseInt(field.id) === parseInt(fieldId),
        );
        if (!field) {
          return acc;
        }

        const replacementKey = `field_${field.key}`;
        let value = null;

        switch (field.type) {
          case "boolean":
            value = translate(
              `eva.main.general.${controller.visitorData[key] ? "yes" : "no"}`,
            );
            break;
          case "select":
            value = translateEntity(
              field.options.find(
                (option) => option.id === controller.visitorData[key],
              ),
              "label",
            );
            break;
          default:
            value = controller.visitorData[key];
            break;
        }

        acc[replacementKey] = value;
        return acc;
      }, {});
  }

  /**
   * This method will fetch the needed relations
   */
  async function fetchNeededRelations() {
    fetchNeededFields();
    fetchNeededEmployee();
  }

  /**
   * This method will fetch the needed fields
   */
  async function fetchNeededFields() {
    const neededFieldIds = Object.keys(controller.visitorData)
      .filter((key) => key.startsWith("field_"))
      .map((key) => key.split("_")[1])
      .filter(
        (id) =>
          !controller.fetchedEntities.fields.find(
            (field) => parseInt(field.id) === parseInt(id),
          ),
      );

    if (neededFieldIds.length > 0) {
      await callEvaApi(
        new EvaApiCall("fields")
          .setLoadingGroup("visitor_type_page_fetch")
          .setParams({
            with_field_options_data: true,
            where: [
              {
                column: "id",
                values: [{ operator: "in", value: neededFieldIds }],
              },
            ],
          })
          .setAlertError(false)
          .setAlertSuccess(false)
          .setOnSuccess((response) => {
            controller.setFetchedEntities((prev) => ({
              ...prev,
              fields: [...prev.fields, ...response.data.data.records],
            }));
          }),
      );
    }
  }

  /**
   * This method will fetch the needed employee
   */
  async function fetchNeededEmployee() {
    const employee = controller.fetchedEntities.employees.find(
      (emp) =>
        parseInt(emp.id) === parseInt(controller.visitorData.employee_id),
    );

    if (!employee) {
      await callEvaApi(
        new EvaApiCall("employees")
          .setLoadingGroup("visitor_type_page_fetch")
          .setParams({
            where: [
              {
                column: "id",
                values: [controller.visitorData.employee_id],
              },
            ],
          })
          .setAlertError(false)
          .setAlertSuccess(false)
          .setOnSuccess((response) => {
            controller.setFetchedEntities((prev) => ({
              ...prev,
              employees: [...prev.employees, ...response.data.data.records],
            }));
          }),
      );
    }
  }

  return <MessagesStepView controller={controller} messages={messages} />;
}

export default MessagesStep;
